// Pure logic for the contextual Ask Masira assistant: which pills to show for a
// section/view, how the scripted report flow advances, and how structured hints
// are parsed out of model replies. No JSX and no imports, so Deno tests can load it.
import { getTemplate, REPORT_TEMPLATES, suggestTemplates, type ReportTemplate, type ReportTemplateInput } from './reportTemplates.ts';

export type PillAction =
  | { type: 'ask'; prompt: string }
  | { type: 'navigate'; view: string }
  | { type: 'menu'; menuId: string }
  | { type: 'home' }
  | { type: 'flow-start'; templateId?: string }
  | { type: 'flow-template'; templateId: string }
  | { type: 'flow-answer'; key: string; value: string; label: string }
  | { type: 'flow-run' }
  | { type: 'flow-cancel' };

export interface Pill {
  id: string;
  label: string;
  action: PillAction;
}

const ask = (label: string, prompt = label): Pill => ({ id: `ask:${label}`, label, action: { type: 'ask', prompt } });
const go = (label: string, view: string): Pill => ({ id: `go:${view}`, label, action: { type: 'navigate', view } });
const menu = (label: string, menuId: string): Pill => ({ id: `menu:${menuId}`, label, action: { type: 'menu', menuId } });
const report: Pill = { id: 'flow:report', label: 'Create a report', action: { type: 'flow-start' } };

/** Pills shown for a view; narrower than the section's. */
const VIEW_PILLS: Record<string, Pill[]> = {
  risks: [menu('What could go wrong?', 'risk-scope'), ask('Which risks are new this week?'), ask('What mitigations are overdue?')],
  projects: [ask('Which projects are off track?'), ask('Which projects are over budget?'), ask('What is blocked right now?')],
  portfolio: [ask('Compare project health'), ask('Where is budget at risk?')],
  signals: [ask('Which signals need action?'), ask('Why was the latest signal raised?')],
  inbox: [ask('What needs a reply today?'), ask('Summarise unread emails')],
  meetings: [ask('What actions came out of recent meetings?'), ask('Prepare me for my next meeting')],
  stakeholders: [ask('Who needs an update?'), ask('Which stakeholders are at risk of disengaging?')],
  decisions: [ask('Which decisions are overdue?'), ask('Summarise recent decisions')],
  hitlWorkbench: [ask('What is waiting for my approval?'), ask('Which approvals are oldest?')],
  reports: [report, ask('Which report suits my audience?')],
  auditTrail: [ask('What changed in the last 24 hours?'), ask('Who approved the latest AI output?')],
};

/** Pills for a section when the view has none of its own. */
const SECTION_PILLS: Record<string, Pill[]> = {
  today: [ask('What needs my attention today?'), ask('What is due this week?'), report],
  communicate: [ask('What needs a reply today?'), ask('Who needs an update?'), ask('Summarise recent meetings')],
  deliver: [ask('Are we on track?'), menu('What could go wrong?', 'risk-scope'), ask('Draft a status update'), report],
  decide: [ask('What decisions are pending?'), ask('What is waiting for my approval?'), ask('Which decisions are overdue?')],
  govern: [ask('What changed recently?'), ask('Which items lack evidence?'), ask('Show compliance gaps')],
  admin: [ask('Which integrations need attention?'), ask('Who has access to what?')],
};

const FALLBACK_PILLS: Pill[] = [ask('What needs my attention today?'), ask('Summarise the biggest project risks'), report];

/** Second-level menus: the assistant narrows the intent before running it. */
const MENUS: Record<string, { question: string; pills: Pill[] }> = {
  'risk-scope': {
    question: 'Which risks should I look at?',
    pills: [
      ask('Top 3 risks', 'List the top 3 open risks across my projects, one line each with severity.'),
      ask('Overdue mitigations', 'Which risk mitigations are overdue? One line each.'),
      ask('Risks by project', 'Group the open risks by project, one line per project.'),
    ],
  },
};

export function menuQuestion(menuId: string): string {
  return MENUS[menuId]?.question ?? 'What would you like to do?';
}

export function menuPills(menuId: string): Pill[] {
  return [...(MENUS[menuId]?.pills ?? []), homePill()];
}

const homePill = (): Pill => ({ id: 'home', label: 'Do something else', action: { type: 'home' } });

export interface ChatContext {
  section?: string;
  view?: string;
  /** Allows hiding pills that open views the role cannot access. */
  canOpen?: (view: string) => boolean;
}

/** Starting pills for the current tab; view-specific first, then the section's, no duplicates. */
export function contextPills(ctx: ChatContext): Pill[] {
  const own = ctx.view ? VIEW_PILLS[ctx.view] ?? [] : [];
  const section = ctx.section ? SECTION_PILLS[ctx.section] ?? [] : [];
  const seen = new Set<string>();
  const merged = [...own, ...(own.length >= 3 ? [] : section)].filter((p) => {
    if (seen.has(p.id)) return false;
    seen.add(p.id);
    return true;
  });
  const allowed = merged.filter((p) => p.action.type !== 'navigate' || !ctx.canOpen || ctx.canOpen(p.action.view));
  return (allowed.length ? allowed : FALLBACK_PILLS).slice(0, 5);
}

/** Pills offered after an answer: always an exit to something else. */
export function followUpPills(ctx: ChatContext, viewLabel?: string): Pill[] {
  const pills: Pill[] = [homePill()];
  if (ctx.view && viewLabel && (!ctx.canOpen || ctx.canOpen(ctx.view))) pills.push(go(`Open ${viewLabel}`, ctx.view));
  pills.push(ask('Tell me more', 'Tell me more about that, with the key details.'));
  return pills;
}

// ---- Scripted report flow ----

export interface ReportFlowState {
  templateId?: string;
  answers: Record<string, string>;
  labels: Record<string, string>;
}

export const emptyReportFlow = (templateId?: string): ReportFlowState => ({ templateId, answers: {}, labels: {} });

export type ReportStep =
  | { kind: 'pickTemplate'; suggestions: ReportTemplate[] }
  | { kind: 'ask'; template: ReportTemplate; input: ReportTemplateInput }
  | { kind: 'confirm'; template: ReportTemplate };

export function nextReportStep(state: ReportFlowState, hint = ''): ReportStep {
  const template = getTemplate(state.templateId);
  if (!template) {
    const suggestions = suggestTemplates(hint);
    return { kind: 'pickTemplate', suggestions: suggestions.length ? suggestions : REPORT_TEMPLATES };
  }
  // A single-option input is answered automatically, so the user is never asked a non-question.
  const input = template.inputs.find((i) => !(i.key in state.answers) && i.options.length > 1);
  if (input) return { kind: 'ask', template, input };
  return { kind: 'confirm', template };
}

export function applyAnswer(state: ReportFlowState, key: string, value: string, label: string): ReportFlowState {
  return { ...state, answers: { ...state.answers, [key]: value }, labels: { ...state.labels, [key]: label } };
}

/** Fills single-option inputs so confirm/run have every value the server needs. */
export function withDefaults(state: ReportFlowState): ReportFlowState {
  const template = getTemplate(state.templateId);
  if (!template) return state;
  let next = state;
  for (const input of template.inputs) {
    if (!(input.key in next.answers) && input.options.length === 1) {
      next = applyAnswer(next, input.key, input.options[0].value, input.options[0].label);
    }
  }
  return next;
}

export function reportStepMessage(step: ReportStep, state: ReportFlowState): string {
  if (step.kind === 'pickTemplate') return 'Which report do you need?';
  if (step.kind === 'ask') return step.input.question;
  const parts = Object.values(state.labels);
  return `Ready to create the ${step.template.name}${parts.length ? ` (${parts.join(', ')})` : ''}. Shall I go ahead?`;
}

export function reportStepPills(step: ReportStep): Pill[] {
  const cancel: Pill = { id: 'flow:cancel', label: 'Cancel', action: { type: 'flow-cancel' } };
  if (step.kind === 'pickTemplate') {
    return [
      ...step.suggestions.map((t) => ({ id: `tpl:${t.id}`, label: t.name, action: { type: 'flow-template', templateId: t.id } as PillAction })),
      cancel,
    ];
  }
  if (step.kind === 'ask') {
    return [
      ...step.input.options.map((o) => ({
        id: `ans:${step.input.key}:${o.value}`,
        label: o.label,
        action: { type: 'flow-answer', key: step.input.key, value: o.value, label: o.label } as PillAction,
      })),
      cancel,
    ];
  }
  return [
    { id: 'flow:run', label: 'Generate report', action: { type: 'flow-run' } },
    { id: 'flow:restart', label: 'Change template', action: { type: 'flow-start' } },
    cancel,
  ];
}

// ---- Structured hints in model replies ----

export interface ParsedReply {
  text: string;
  options: string[];
  flow?: 'report';
}

/**
 * The model may end a reply with `[[options: A | B | C]]` (clarifying choices) and/or
 * `[[flow: report]]`. Strips them (including a half-streamed marker) and returns them.
 */
export function parseAssistantReply(raw: string): ParsedReply {
  let text = raw;
  const options: string[] = [];
  let flow: 'report' | undefined;

  for (const m of raw.matchAll(/\[\[\s*options\s*:\s*([^\]]*?)\s*\]\]/gi)) {
    options.push(...m[1].split('|').map((o) => o.trim()).filter(Boolean));
  }
  if (/\[\[\s*flow\s*:\s*report\s*\]\]/i.test(raw)) flow = 'report';

  text = text.replace(/\[\[[^\]]*\]\]/g, '');
  const open = text.lastIndexOf('[[');
  if (open !== -1) text = text.slice(0, open);
  return { text: text.trim(), options: options.slice(0, 4), flow };
}
